(() => {
  if (window.__jrmLaiResponsiveImagesInitialized) return;
  window.__jrmLaiResponsiveImagesInitialized = true;

  const isProductPage = document.body?.classList.contains('hdt-page-type-product');

  const CAROUSEL_CONFIG = {
    widths: [360, 540, 720, 900, 1080],
    sizes: '(min-width: 1200px) 337px, (min-width: 768px) 33vw, calc(100vw - 32px)'
  };

  const PDP_CONFIG = {
    widths: [80, 120, 160, 180, 240, 320],
    sizes: '60px'
  };

  const PDP_FRAME_SELECTOR = 'iframe.scm-reviews-importer-iframe.sma-load-iframe';
  const POPUP_FRAME_ID = 'scm-review-importer-popup-iframe';

  const debug = {
    version: '3D-A4+3D-B2-pdp-iframe',
    loaded: true,
    startedAt: new Date().toISOString(),
    pageContext: isProductPage ? 'pdp-review-thumbnail' : 'carousel',
    reviewImagesSeen: 0,
    carouselApplied: 0,
    pdpThumbnailApplied: 0,
    framesSeen: 0,
    framesAttached: 0,
    frameReloads: 0,
    frameAccessErrors: 0,
    lastError: null
  };

  window.__jrmLaiResponsiveImagesDebug = debug;

  const seenImages = new WeakSet();
  const seenFrames = new WeakSet();
  const loadBoundFrames = new WeakSet();
  const frameStates = new WeakMap();

  function isImage(node) {
    return Boolean(node && node.nodeType === 1 && node.tagName === 'IMG');
  }

  function isReviewImage(img, baseURI) {
    if (!isImage(img) || !img.classList.contains('img-rv')) return false;

    const raw = img.getAttribute('src') || img.currentSrc || '';
    if (!raw) return false;

    try {
      const url = new URL(raw, baseURI || document.baseURI);
      const isShopifyImage =
        url.hostname === 'cdn.shopify.com' ||
        url.hostname.endsWith('.shopifycdn.com') ||
        url.pathname.includes('/cdn/shop/');

      return isShopifyImage && /\/review-/i.test(url.pathname);
    } catch (error) {
      debug.lastError = `isReviewImage: ${error.message}`;
      return false;
    }
  }

  function buildUrl(original, width, baseURI) {
    const url = new URL(original, baseURI || document.baseURI);
    url.searchParams.delete('width');
    url.searchParams.set('width', String(width));
    return url.toString();
  }

  function optimizeImage(img, config, context, baseURI) {
    if (!isReviewImage(img, baseURI)) return;

    if (!seenImages.has(img)) {
      seenImages.add(img);
      debug.reviewImagesSeen += 1;
    }

    const original = img.getAttribute('src');
    if (!original) return;

    const existingSrcset = img.getAttribute('srcset');

    // Fail open if LAI starts providing its own responsive markup later.
    if (existingSrcset && img.dataset.jrmResponsive !== '1') return;

    if (
      img.dataset.jrmResponsive === '1' &&
      img.dataset.jrmResponsiveContext === context &&
      img.dataset.jrmResponsiveSource === original &&
      img.getAttribute('srcset') &&
      img.getAttribute('sizes') === config.sizes
    ) {
      return;
    }

    try {
      const srcset = config.widths
        .map((width) => `${buildUrl(original, width, baseURI)} ${width}w`)
        .join(', ');

      // Preserve LAI's original src and native/custom lazy-loading behavior.
      img.setAttribute('sizes', config.sizes);
      img.setAttribute('srcset', srcset);
      img.dataset.jrmResponsive = '1';
      img.dataset.jrmResponsiveContext = context;
      img.dataset.jrmResponsiveSource = original;

      if (context === 'pdp-review-thumbnail') debug.pdpThumbnailApplied += 1;
      else debug.carouselApplied += 1;
    } catch (error) {
      debug.lastError = `optimizeImage: ${error.message}`;
    }
  }

  function scanRoot(root, config, context, baseURI) {
    if (!root) return;

    if (isImage(root)) optimizeImage(root, config, context, baseURI);
    root.querySelectorAll?.('img.img-rv').forEach((img) => {
      optimizeImage(img, config, context, baseURI);
    });
  }

  function createImageObserver(root, config, context, baseURI) {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) scanRoot(node, config, context, baseURI);
          });
          continue;
        }

        if (mutation.type === 'attributes' && isImage(mutation.target)) {
          optimizeImage(mutation.target, config, context, baseURI);
        }
      }
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'class', 'srcset', 'sizes']
    });

    return observer;
  }

  function startHomepageCarousel() {
    scanRoot(document, CAROUSEL_CONFIG, 'carousel', document.baseURI);
    createImageObserver(
      document.documentElement,
      CAROUSEL_CONFIG,
      'carousel',
      document.baseURI
    );
  }

  function isTargetPdpFrame(frame) {
    return Boolean(
      frame &&
      frame.tagName === 'IFRAME' &&
      frame.id !== POPUP_FRAME_ID &&
      frame.matches(PDP_FRAME_SELECTOR)
    );
  }

  function attachPdpFrame(frame) {
    if (!isTargetPdpFrame(frame)) return false;

    if (!seenFrames.has(frame)) {
      seenFrames.add(frame);
      debug.framesSeen += 1;
    }

    let doc;

    try {
      doc = frame.contentDocument;
    } catch (error) {
      debug.frameAccessErrors += 1;
      debug.lastError = `frameAccess: ${error.message}`;
      return false;
    }

    const root = doc?.documentElement;
    if (!doc || !root) return false;

    const prior = frameStates.get(frame);

    if (prior && prior.doc === doc && prior.root === root) {
      scanRoot(root, PDP_CONFIG, 'pdp-review-thumbnail', doc.baseURI);
      return true;
    }

    if (prior?.observer) {
      prior.observer.disconnect();
      debug.frameReloads += 1;
    }

    scanRoot(root, PDP_CONFIG, 'pdp-review-thumbnail', doc.baseURI);

    const observer = createImageObserver(
      root,
      PDP_CONFIG,
      'pdp-review-thumbnail',
      doc.baseURI
    );

    frameStates.set(frame, { doc, root, observer });
    debug.framesAttached += 1;
    return true;
  }

  function bindPdpFrame(frame) {
    if (!isTargetPdpFrame(frame)) return;

    if (!loadBoundFrames.has(frame)) {
      loadBoundFrames.add(frame);
      frame.addEventListener('load', () => {
        // srcdoc may replace the iframe document while reusing the same element.
        requestAnimationFrame(() => attachPdpFrame(frame));
      });
    }

    attachPdpFrame(frame);
  }

  function scanForPdpFrames(root) {
    if (!root) return;

    if (root.nodeType === 1 && root.tagName === 'IFRAME') {
      bindPdpFrame(root);
    }

    root.querySelectorAll?.(PDP_FRAME_SELECTOR).forEach(bindPdpFrame);
  }

  function startPdpReviews() {
    scanForPdpFrames(document);

    const topObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) scanForPdpFrames(node);
          });
          continue;
        }

        if (mutation.type === 'attributes' && mutation.target?.tagName === 'IFRAME') {
          bindPdpFrame(mutation.target);
        }
      }
    });

    topObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'srcdoc']
    });

    // LAI can rewrite about:srcdoc while retaining the same iframe element.
    // Short-lived rechecks make that lifecycle fail-open without permanent polling.
    let checks = 0;
    const recheckTimer = window.setInterval(() => {
      checks += 1;
      document.querySelectorAll(PDP_FRAME_SELECTOR).forEach(attachPdpFrame);
      if (checks >= 32) window.clearInterval(recheckTimer);
    }, 250);
  }

  if (isProductPage) startPdpReviews();
  else startHomepageCarousel();

  debug.rescan = () => {
    if (isProductPage) {
      scanForPdpFrames(document);
      document.querySelectorAll(PDP_FRAME_SELECTOR).forEach(attachPdpFrame);
    } else {
      scanRoot(document, CAROUSEL_CONFIG, 'carousel', document.baseURI);
    }

    return {
      version: debug.version,
      pageContext: debug.pageContext,
      reviewImagesSeen: debug.reviewImagesSeen,
      carouselApplied: debug.carouselApplied,
      pdpThumbnailApplied: debug.pdpThumbnailApplied,
      framesSeen: debug.framesSeen,
      framesAttached: debug.framesAttached,
      frameReloads: debug.frameReloads,
      frameAccessErrors: debug.frameAccessErrors,
      lastError: debug.lastError
    };
  };
})();
